import { Button, Icon, Spinner, Text, Tooltip } from '@nike/eds'
import { useEffect, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { logUsage, RebookingApproveFilter } from '../../services/ShipmentService'
import { useQueryState } from '../../common/hooks/useQueryState'
import { useShipmentList, useOpenRebookingTypes } from '../../common/hooks/useShipmentQueries'
import Chips from '../../common/components/chips/Chips'
import { TableLayout } from '../../common/components/table'
import { Divider } from '@mui/material'
import { Navigate } from 'react-router-dom'
import { removeEmptyAndDuplicateFields } from '../../common/utils/datautils/sanitize'
import useRoleContext from '../../common/hooks/useRoleContext'
import { ApproveModal } from '../../pages/approvepage/approvemodal/ApproveModal'
import { DeclineModal } from './declinemodal/DeclineModal'
import { RebookingHistoryRow } from '../../common/components/rebookinghistoryrow/RebookingHistoryRow'
import { ReportType } from '../../common/utils/ReportType'
import { DownloadModal } from '../../common/components/downloadmodal/DownloadModal'
import { ApproveFilterForm } from './approvefilterform/ApproveFilterForm'
import { useFilters } from '../../common/hooks/useFilters'
import { ApplicationSettings, fetchUserPreferences } from '../../services/UserPreferencesService'
import { useApproveRebookingTable } from './approverebookingtable/ApproveRebookingTable'

const pageSizeOptions = [10, 25, 50, 100, 200, 500]

const RebookingApprove = () => {
  const {
    state: queryState,
    setFilter,
    gotoPage,
    setPageSize,
    toggleSortBy,
    clearFilter,
  } = useQueryState<RebookingApproveFilter>({ filter: { open: true }, pageSize: 200 })
  const { authState } = useOktaAuth()
  const { isFetching, isError, isSuccess, data, isRefetching, refetch } =
    useShipmentList(queryState)
  const {
    isSuccess: isSuccessRebookingTypes,
    data: dataRebookingTypes
  } =
    useOpenRebookingTypes(queryState)
  const [openRebookingCreationDateTime, setOpenRebookingCreationDateTime] = useState<
    Date | undefined
  >(undefined)
  const [expandFilterForm, setExpandFilterForm] = useState(false)
  const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false)
  const [exportModalOpen, setExportModalOpen] = useState<boolean>(false)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [declineModalOpen, setDeclineModalOpen] = useState<boolean>(false)
  const isExportButtonDisabled = isFetching || isDownloading || isError
  const { isSuccess: isFiltersFetched } = useFilters(authState, 'approve')

  useEffect(() => {
    const applyFilters = async () => {
      let applicationSettings: ApplicationSettings = await fetchUserPreferences(
        authState,
        'approve',
        'filter'
      )
      if (applicationSettings.filters) {
        setFilter(applicationSettings.filters.filter(filter => filter.default)[0].filter)
      }
    }
    if (isFiltersFetched) {
      applyFilters()
    } else {
      return undefined
    }
    // eslint-disable-next-line
  }, [isFiltersFetched])

  useEffect(() => {
    if (queryState.filter) {
      logUsage('approve', 'search', { filters: removeEmptyAndDuplicateFields(queryState.filter) })
    }
  }, [queryState.filter])
  useEffect(() => {
    if (!isRefetching) {
      setOpenRebookingCreationDateTime(new Date())
    }
  }, [isRefetching])
  // Table
  const tableInstance = useApproveRebookingTable(
    queryState,
    gotoPage,
    setPageSize,
    toggleSortBy,
    isFetching,
    isSuccess,
    isError,
    data
  )

  let multipleTypesSelected = false

  if (tableInstance.isAllServerRowsSelected && isSuccessRebookingTypes) {
    multipleTypesSelected = dataRebookingTypes?.data.length > 1
  } else {
    multipleTypesSelected = new Set(tableInstance.selectedFlatRows.map(row =>
    (row.original['rebookingTypeData'] as Map<string, any>)['type'])).size > 1
  }

  const selectedItemsForApproval = Object.keys(tableInstance.state.selectedRowIds).length > 0
  const selectedItemsForDecline = selectedItemsForApproval && !multipleTypesSelected

  /* Start filter form */
  const onClickFilter = (filterFormData: RebookingApproveFilter) => {
    setFilter(filterFormData)
  }

  const handleCloseFilterForm = () => {
    setExpandFilterForm(false)
  }

  const handleCloseDeclineModalForm = () => {
    setDeclineModalOpen(false)
  }
  /* End filter form */

  const hasApprovalAccess = useRoleContext().hasApprovalAccess()

  if (!hasApprovalAccess) {
    // no Approval access - redirect to root and let the router handle the correct redirect
    return <Navigate to="/" replace />
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--eds-space-8)',
        }}
      >
        <div
          className="flex-row flex-nowrap content-space-between justify-items-start"
          style={{ padding: 'var(--eds-space-16) var(--eds-space-16) 0 var(--eds-space-16)' }}
        >
          <div
            style={{
              width: '90%',
            }}
          >
            <Chips setFilter={setFilter} queryState={queryState} clearFilter={clearFilter} />
          </div>
          <div className="flex-shrink-0">
            <Button
              style={{ marginTop: 'var(--eds-space-8)', paddingLeft: 'var(--eds-space-16)' }}
              onClick={() => setExpandFilterForm(true)}
              disabled={isFetching}
              beforeSlot={<Icon name="Filter" size="m" />}
            >
              Filters
            </Button>
          </div>
        </div>

        <Divider
          style={{
            width: '99%',
            margin: 'auto',
            borderBottomWidth: 2,
          }}
        />

        <div
          className="flex-row flex-nowrap content-space-between justify-items-center"
          style={{ padding: '0 var(--eds-space-16)' }}
        >
          <div>
            <Tooltip
              isDark
              placement={'right'}
              hidden={selectedItemsForApproval}
              strategy="fixed"
              bodySlot={<Text as={'p'}>At least one selected record is required.</Text>}
            >
              <Button
                size="medium"
                style={{ marginRight: 'var(--eds-space-8)' }}
                disabled={!selectedItemsForApproval}
                onClick={() => setApproveModalOpen(true)}
              >
                Approve
              </Button>
            </Tooltip>

            <Tooltip
              isDark
              placement={'right'}
              hidden={selectedItemsForDecline}
              strategy="fixed"
              bodySlot={
                <Text as={'p'}>
                  At least one selected record is required.
                  <br />
                  All selected records must have the same rebooking type.
                  <br />
                  Note that the 'Select all' checkbox selects rebookings across pages so you might
                  select multiple rebooking types without it being visible.
                </Text>
              }
            >
              <Button
                size="medium"
                style={{ marginRight: 'var(--eds-space-8)' }}
                disabled={!selectedItemsForDecline}
                onClick={() => setDeclineModalOpen(true)}
              >
                Decline
              </Button>
            </Tooltip>
          </div>

          <Button
            variant="primary"
            className="Button"
            style={{ paddingLeft: 'var(--eds-space-16)' }}
            disabled={isExportButtonDisabled}
            onClick={() => setExportModalOpen(true)}
            afterSlot={isDownloading ? <Spinner color="white" /> : undefined}
            beforeSlot={<Icon name="Download" size="m" />}
          >
            Export{isDownloading && 'ing'} Results
          </Button>
        </div>

        <TableLayout
          {...{ ...tableInstance, pageSizeOptions, isFetching, isError }}
          queryState={queryState}
          className="-striped -highlight"
          refetchData={refetch}
          expandedRow={RebookingHistoryRow}
        />
      </div>
      <ApproveFilterForm
        currentFilter={queryState.filter}
        onClickFilter={onClickFilter}
        expand={expandFilterForm}
        onCloseSidePanel={handleCloseFilterForm}
      />
      <DeclineModal
        declineModalOpen={declineModalOpen}
        onCloseRebookingModal={handleCloseDeclineModalForm}
        isAllRowsSelected={!!tableInstance.isAllServerRowsSelected}
        totalResources={tableInstance.totalResources}
        filter={queryState.filter}
        selectedRows={tableInstance.selectedFlatRows}
        openRebookingCreationDateTime={openRebookingCreationDateTime}
      />

      <ApproveModal
        approveModalOpen={approveModalOpen}
        setApproveModalOpen={setApproveModalOpen}
        isAllRowsSelected={!!tableInstance.isAllServerRowsSelected}
        totalResources={tableInstance.totalResources}
        filter={queryState.filter}
        selectedRows={tableInstance.selectedFlatRows}
        openRebookingCreationDateTime={openRebookingCreationDateTime}
      />
      <DownloadModal
        exportModalOpen={exportModalOpen}
        setExportModalOpen={setExportModalOpen}
        queryState={queryState}
        isDownloading={isDownloading}
        setIsDownloading={setIsDownloading}
        reportType={ReportType.REBOOKING_APPROVAL_REPORT}
        totalResults={data?.totalResources}
      />
    </>
  )
}

export default RebookingApprove
